import { MapContainer, TileLayer, Marker, CircleMarker, Popup, Tooltip, Circle, Polyline, useMapEvents, useMap } from "react-leaflet";
import { useEffect, useRef } from "react";
import L from "leaflet";
import { STATUS_COLORS } from "./StatusBadge";
import MapBaseLayers from "./MapBaseLayers";

// Minimal vehicle-type glyphs (fill=white), rotated by heading
const GLYPH = {
  Truck: '<path d="M2 6h11v7H2z"/><path d="M13 8h4l3 3v2h-7z"/><circle cx="6" cy="16" r="1.7"/><circle cx="16" cy="16" r="1.7"/>',
  Trailer: '<path d="M1 6h17v7H1z"/><circle cx="5" cy="16" r="1.4"/><circle cx="9" cy="16" r="1.4"/><circle cx="14" cy="16" r="1.4"/>',
  Tractor: '<circle cx="7" cy="15" r="3.2"/><circle cx="16" cy="16" r="1.7"/><path d="M5 7h5v5H5z"/>',
  Car: '<path d="M3 12l2-4h9l3 4v3H3z"/><circle cx="6" cy="16" r="1.5"/><circle cx="15" cy="16" r="1.5"/>',
  Puller: '<path d="M4 7h6v6H4z"/><path d="M10 9h4l3 4v0h-7z"/><circle cx="7" cy="15" r="1.6"/><circle cx="15" cy="15" r="1.6"/>',
  Tanker: '<rect x="2" y="7" width="13" height="6" rx="3"/><circle cx="6" cy="16" r="1.5"/><circle cx="12" cy="16" r="1.5"/>',
  Bus: '<rect x="2" y="6" width="15" height="8" rx="1.5"/><circle cx="6" cy="16" r="1.5"/><circle cx="14" cy="16" r="1.5"/>',
};

function vehicleIcon(type, status, heading = 0, selected = false) {
  const color = STATUS_COLORS[status] || "#8E8E93";
  const g = GLYPH[type] || GLYPH.Car;
  const ring = selected ? "box-shadow:0 0 0 3px rgba(124,58,183,0.6);" : "";
  const pulse = status === "panic" ? "animation:pulse-ring 1.5s infinite;" : "";
  const html =
    `<div style="width:30px;height:30px;transform:rotate(${(heading || 0) - 90}deg);">
       <div style="width:30px;height:30px;background:${color};border:2px solid #fff;border-radius:7px;
                   display:flex;align-items:center;justify-content:center;${ring}${pulse}">
         <svg width="18" height="18" viewBox="0 0 20 20" fill="#fff">${g}</svg>
       </div>
     </div>`;
  return L.divIcon({ html, className: "veh-marker", iconSize: [30, 30], iconAnchor: [15, 15] });
}

function MapClicker({ onMapClick }) {
  useMapEvents({ click(e) { onMapClick && onMapClick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

// Auto-center the map on a selected moving vehicle (Follow mode)
function FollowVehicle({ vehicles, followId }) {
  const map = useMap();
  const prev = useRef(null);
  useEffect(() => {
    if (!followId) { prev.current = null; return; }
    const v = vehicles.find((x) => x.id === followId);
    if (!v) return;
    if (prev.current !== followId) {
      map.setView([v.lat, v.lng], Math.max(map.getZoom(), 15), { animate: true });
      prev.current = followId;
    } else {
      map.panTo([v.lat, v.lng], { animate: true, duration: 1 });
    }
  }, [vehicles, followId, map]);
  return null;
}

export default function FleetMap({
  vehicles = [], center = [21.5, 78.9], zoom = 5, selectedId, onSelect,
  geofences = [], routes = [], pois = [], trail = null, trails = {}, followId = null,
  onMapClick, height = "100%",
}) {
  return (
    <div data-testid="fleet-map" style={{ height, width: "100%", position: "relative" }}>
      <div className="map-live-badge" data-testid="map-live-badge"><span className="dot" /> LIVE · {vehicles.length}</div>
      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom preferCanvas>
        <MapBaseLayers />
        {onMapClick && <MapClicker onMapClick={onMapClick} />}
        <FollowVehicle vehicles={vehicles} followId={followId} />
        {Object.entries(trails).map(([id, pts]) => (pts && pts.length > 1) && (
          <Polyline key={`trail-${id}`} positions={pts}
            pathOptions={{ color: id === selectedId ? "#245fc7" : "#8aa4c8",
                           weight: id === selectedId ? 3 : 2, opacity: id === selectedId ? 0.85 : 0.55 }} />
        ))}
        {routes.map((r) => (
          <Polyline key={r.id} positions={r.points} pathOptions={{ color: "#FF9500", weight: 3, dashArray: "6 6" }}>
            <Tooltip>{r.name}</Tooltip>
          </Polyline>
        ))}
        {geofences.map((g) => (
          <Circle key={g.id} center={[g.center_lat, g.center_lng]} radius={g.radius_m}
            pathOptions={{ color: "#7C3AED", fillColor: "#7C3AED", fillOpacity: 0.08, weight: 1 }}>
            <Tooltip>{g.name}</Tooltip>
          </Circle>
        ))}
        {trail && trail.length > 1 && (
          <Polyline positions={trail} pathOptions={{ color: "#7C3AED", weight: 3 }} />
        )}
        {pois.map((p) => (
          <CircleMarker key={p.id} center={[p.lat, p.lng]} radius={5}
            pathOptions={{ color: "#FFD60A", fillColor: "#FFD60A", fillOpacity: 1, weight: 2 }}>
            <Tooltip permanent direction="top" offset={[0, -4]} className="poi-tip">{p.name}</Tooltip>
          </CircleMarker>
        ))}
        {vehicles.map((v) => (
          <Marker key={v.id} position={[v.lat, v.lng]}
            icon={vehicleIcon(v.vehicle_type || "Car", v.status, v.heading, v.id === selectedId)}
            eventHandlers={{ click: () => onSelect && onSelect(v) }}>
            <Tooltip direction="top" offset={[0, -12]}>
              <span className="font-semibold">{v.vehicle_number}</span> · {v.speed} km/h
            </Tooltip>
            <Popup>
              <div className="text-xs">
                <div className="font-bold text-sm">{v.vehicle_number}</div>
                <div>{v.vehicle_type} · {v.device_model}</div>
                <div>Speed: {v.speed} km/h · {v.status}</div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

export { STATUS_COLORS };
